import { Dialog } from "@resource/atlas";
import { RichBlockProviderOrganization } from "client/guide-content/rich-blocks/provider";
import { PostTemplateForManagingFragment } from "generated/schemaTypes";
import { useRef, useState } from "react";

import { PostTemplateContentDialogView } from "./PostTemplateContentDialogView";
import { useDialogStateFormManagement } from "./useDialogStateFormManagement";
import { useDialogWithLeaveConfirmation } from "./useDialogWithLeaveConfirmation";
import { useContentForm } from "./useForms";
import { useUpsertPostTemplate } from "./useUpsertPostTemplate";

type EditContentPostTemplateDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSavePostTemplate?: (postTemplate: PostTemplateForManagingFragment) => void;
  postTemplate: PostTemplateForManagingFragment;
  organization: RichBlockProviderOrganization;
};

export function EditContentPostTemplateDialog({
  open,
  setOpen,
  organization,
  postTemplate,
  onSavePostTemplate,
}: EditContentPostTemplateDialogProps) {
  const initialFocusRef = useRef<HTMLInputElement>(null);
  const [upsertPostTemplateLoading, setUpsertPostTemplateLoading] =
    useState(false);
  const [upsertPostTemplate] = useUpsertPostTemplate();

  const { useFormReturn, showConfirmation } = useContentForm({
    postTemplate,
  });

  const dialogState = useDialogWithLeaveConfirmation({
    open,
    setOpen,
    showConfirmation,
  });

  useDialogStateFormManagement({
    dialogState,
    useFormReturn,
  });

  return (
    <Dialog.Root state={dialogState}>
      <Dialog.Content
        initialFocusRef={initialFocusRef}
        size="medium"
        // Disabled due to bug when used with useLeaveConfirmation
        hideOnInteractOutside={false}
      >
        <PostTemplateContentDialogView
          analyticsType="edit"
          title={`Edit ${postTemplate.name} message template`}
          submitButtonText="Save"
          onSubmit={(submittedSettingsData) => {
            setUpsertPostTemplateLoading(true);
            upsertPostTemplate({
              variables: {
                input: {
                  id: postTemplate.id,
                  title: submittedSettingsData.title,
                  data: submittedSettingsData.data,
                },
              },
              onCompleted: (result) => {
                dialogState.hide();
                setUpsertPostTemplateLoading(false);
                onSavePostTemplate?.(result.upsertPostTemplate.postTemplate);
              },
            });
          }}
          submitLoading={upsertPostTemplateLoading}
          initialFocusRef={initialFocusRef}
          useFormReturn={useFormReturn}
          dialogState={dialogState}
          organization={organization}
        />
      </Dialog.Content>
    </Dialog.Root>
  );
}
